import Dexie, {IndexableType, Table} from 'dexie';
import {BehaviorSubject} from 'rxjs';

import {StorageKeys} from '@core/storage';
import {patchState, signalState} from '@ngrx/signals';
import {storageSignal} from '@shared/signals-extension';

import {globalSignalStoreInitialState, GlobalSignalStoreState, GlobalsSignalStoresConfig, SignalStoreState} from '../data';
import {setSignalStoreDexieIdbVersions} from './dexie-idb-versions';

export class SignalStoreDexieIDB extends Dexie {
	/* ------------------------------------------------  Statics ------------------------------------------------ */
	public static readonly SIGNAL_STORE_DB_NAME = 'dc-signal-store-db' as const;
	public static readonly SIGNAL_STORE_TABLE_NAME = 'signal-stores' as const;
	/* -----------------------------------------------  Singleton ----------------------------------------------- */
	private static instance: SignalStoreDexieIDB = null;
	/* -----------------------------------------------  Properties ----------------------------------------------- */
	public readonly DB_STATE = signalState<GlobalSignalStoreState>(undefined);
	public readonly storeLoaded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public readonly DB_STATE_CONFIG = signalState<GlobalsSignalStoresConfig>(undefined);
	// we will use a stream to notify when the db is open and the table is loaded
	private patchQueue: {storeName: string; state: SignalStoreState}[] = [];
	private isPatchQueueProcessing = false;
	private _enableLogging$ = storageSignal(StorageKeys.Logging.SIGNAL_STORE_DEBUG, false);

	protected constructor() {
		super(SignalStoreDexieIDB.SIGNAL_STORE_DB_NAME);
		const lattesVern = setSignalStoreDexieIdbVersions.bind(this)();
		this.log('DexieIDB version:', lattesVern);
	}

	public get signalsStoreTable(): Table<any, IndexableType> {
		return this.table(SignalStoreDexieIDB.SIGNAL_STORE_TABLE_NAME);
	}

	public static getInstance<T extends SignalStoreState>(): SignalStoreDexieIDB {
		if (SignalStoreDexieIDB.instance === null) SignalStoreDexieIDB.instance = new SignalStoreDexieIDB();
		return SignalStoreDexieIDB.instance;
	}

	/* ------------------------------- Load Stores ------------------------------ */

	public setSyncableStoreConfig(storeName: keyof GlobalsSignalStoresConfig, config: GlobalsSignalStoresConfig[typeof storeName]) {
		if (this.DB_STATE_CONFIG() === undefined) patchState(this.DB_STATE_CONFIG, {});
		patchState(this.DB_STATE_CONFIG, {[storeName]: config});
	}

	/**
	 * This method is called when the user logs out.
	 * It removes all listeners, clears the DexieIDB instance, and resets the indicators.
	 */
	public async onLogout() {
		const storesConfig = this.DB_STATE_CONFIG();
		// if (!storesConfig) return;
		const storesToClear = Object.keys(storesConfig).filter(storeName => {
			const clearOn = storesConfig[storeName].clearOn || 'logout';
			return clearOn === 'logout';
		});
		// Clear all stores on logout
		await this.clearStore(storesToClear);
		patchState(this.DB_STATE, globalSignalStoreInitialState);
	}

	public async setEventListeners() {
		/** `populate` event is triggered when the database is opened for the first time (version change, or first time) */
		this.on('populate', async () => {
			this.log('`populate` event triggered');
			await this.loadAllStores();
		});

		/** `versionchange` event is triggered when the database version changes */
		this.on('versionchange', async () => {
			this.log('`versionchange` event triggered');
			await this.loadAllStores();
		});

		/** `ready` event is triggered when the database is opened */
		this.on('ready', async () => {
			this.log('`ready` event triggered');
			await this.loadAllStores();
		});

		/** `blocked` event is triggered when the database is blocked by another database connection */
		this.on('blocked', async () => {
			this.log('`blocked` event triggered');
		});
		/** `close` event is triggered when the database is closed */
		this.on('close', async () => {
			this.log('`close` event triggered');
		});

		await this.open()
			.catch(console.error)
			.then((v: Dexie) => {
				this.log('DexieIDB opened', v);
			});
	}

	/* ------------------------------- DB Patching ------------------------------ */
	// we want to save all waiting patches in a queue, and apply them when the db and the table are loaded:

	/**
	 * Load all stores from the db
	 * @private
	 * @algorithm:
	 * 1. before load the stores, we need to retrieve that db's table that holds the stores
	 * 2. then, in parallel, we load all stores from the table
	 * 3. after loading all stores, we set the `storeLoaded$` to true (notify that all stores are loaded)
	 */
	public async loadAllStores(): Promise<void> {
		if (this.storeLoaded$.value) return;
		// loading all stores parallel:
		await Promise.all(
			Object.entries(globalSignalStoreInitialState).map(([storeName, stateWithConfig]) => {
				return this.loadSingleStore(this.signalsStoreTable, storeName, stateWithConfig);
			}),
		);
		this.storeLoaded$.next(true);
	}

	public clearStore(storeNames: string[]): Promise<void> {
		return this.signalsStoreTable.bulkDelete(storeNames);
	}

	/**
	 * Patch a store in the db
	 * @param storeName
	 * @param state
	 * @public
	 * @algorithm:
	 * 1.	if the db is not loaded, we push the patch data to the queue
	 * 2.	else, we push the patch data to the queue and process the queue (to process all tasks in the queue)
	 */
	public patchIdbStore<T extends SignalStoreState>(storeName: string, state: T) {
		if (!this.storeLoaded$.value) {
			this.patchQueue.push({storeName, state});
			return;
		}

		this.patchQueue.push({storeName, state});
		this.processPatchQueue().then(r => {});
	}

	/**
	 * Load a single store from the db
	 * @param storesTable
	 * @param storeName
	 * @param stateWithConfig
	 * @private
	 * @algorithm:
	 * 1. wait for the store entry from the db table.
	 * 2. if the store	is not found, we save the initial state to the db, and patch the store state to the global state.
	 * 3. else, we patch the store state to the global state.
	 */
	private async loadSingleStore<T extends GlobalSignalStoreState[keyof GlobalSignalStoreState]>(storesTable: Table<any, IndexableType, any>, storeName: string, stateWithConfig: T) {
		const store = await storesTable.get(storeName);
		const {state, config} = stateWithConfig;
		if (!store) {
			await storesTable.put({storeName, state}, storeName);
			patchState(this.DB_STATE, {[storeName]: state});
			patchState(this.DB_STATE_CONFIG, {[storeName]: config});
		} else {
			patchState(this.DB_STATE, {[storeName]: store.state});
			patchState(this.DB_STATE_CONFIG, {[storeName]: config});
		}
	}

	private async processPatchQueue() {
		if (this.isPatchQueueProcessing) return;
		this.isPatchQueueProcessing = true;

		while (this.patchQueue.length > 0) {
			const {storeName, state} = this.patchQueue.shift();
			await this.signalsStoreTable.put({storeName, state}, storeName);
			patchState(this.DB_STATE, {[storeName]: state});
		}

		this.isPatchQueueProcessing = false;
	}

	private log(...args: any[]) {
		this._enableLogging$() && console.debug('[SignalStoreDexieIDB]', ...args);
	}
}
